import React, { useEffect, useState, useRef } from 'react';
import { Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js/auto';
import { useNavigate } from 'react-router-dom';
import AlertsTable from '../components/AlertsTable';
import TimeFilterButtons from '../components/TimeFilterButtons';
import StateFilterButtons from '../components/StateFilterButtons';
import AlertDetailsModal from '../components/AlertDetailsModal';
import FiltersForm from '../components/FiltersForm';
import './QuestionsPage.css'
import { useAuth } from '../AuthContext';
import { useSearchParams } from 'react-router-dom';

// Dashboard Component
function Dashboard({ stats, filteredAlerts, timeInterval }) {
  const getStatsForInterval = () => {
    if (!stats) return { total: 0, alerts: 0 };

    return stats[timeInterval] || { total: 0, alerts: 0 };
  };

  const statsData = getStatsForInterval();
  const ignoredCount = statsData.total - filteredAlerts.length;
  const ignoredPercentage = statsData.total > 0 ? ((ignoredCount / statsData.total) * 100).toFixed(5) : 0;

  // Calculate false alarms percentage from filtered alerts
  const falseAlarmCount = filteredAlerts.filter(alert => alert.state === 'FA').length;
  const falseAlarmPercentage = filteredAlerts.length > 0 ? ((falseAlarmCount / statsData.total) * 100).toFixed(5) : 0;

  // Calculate processed alerts (alerts that are not new - have a state)
  const processedCount = filteredAlerts.filter(alert => alert.state != null).length;

  return (
    <div className="dashboard" style={{
      display: 'flex',
      gap: '20px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    }}>
      <div className="dashboard-card" style={{
        flex: '1',
        minWidth: '250px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        textAlign: 'center'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#6c757d', fontSize: '14px', fontWeight: 'normal' }}>
          Alert Filtering Rate
        </h4>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff', marginBottom: '5px' }}>
          {filteredAlerts.length} / {statsData.total}
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d' }}>
          {ignoredPercentage}% ignored ({ignoredCount} events)
        </div>
      </div>

      <div className="dashboard-card" style={{
        flex: '1',
        minWidth: '250px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        textAlign: 'center'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#6c757d', fontSize: '14px', fontWeight: 'normal' }}>
          False Alarm Rate
        </h4>
        <div style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '5px' }}>
          <span style={{ fontSize: '32px' }}>{String(falseAlarmPercentage).split('.')[0]}</span>
          <span style={{ fontSize: '20px' }}>.{String(falseAlarmPercentage).split('.')[1] || '0'}%</span>
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d' }}>
          {falseAlarmCount} out of {statsData.total} alerts
        </div>
      </div>

      <div className="dashboard-card" style={{
        flex: '1',
        minWidth: '250px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        textAlign: 'center'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#6c757d', fontSize: '14px', fontWeight: 'normal' }}>
          Processed Alerts
        </h4>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>
          {filteredAlerts.length > 0 ? ((processedCount / filteredAlerts.length) * 100).toFixed(1) : 0}%
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d' }}>
          {processedCount} out of {filteredAlerts.length} alerts triggered
        </div>
      </div>
    </div>
  );
}

function AlertsPage({ refreshNotifications }) {
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [stats, setStats] = useState(null); // New state for stats
  const [filters, setFilters] = useState({
    organization_hostname: '',
    peer_hostname: '',
    description: '',
  });
  const [stateFilter, setStateFilter] = useState('Not Closed');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [timeInterval, setTimeInterval] = useState('today');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [alertToMarkFalse, setAlertToMarkFalse] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const containerRef = useRef(null);
  const pieChartRef = useRef(null);
  const navigate = useNavigate();
  const { API_BASE_URL } = useAuth();
  const [searchParams] = useSearchParams();

  // Fetch stats function
  const fetchStats = () => {
    const token = localStorage.getItem('token');
    const url = `${API_BASE_URL}stats`;

    fetch(url, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.data) {
          setStats(data.data);
        }
      })
      .catch((err) => {
        console.error('Error fetching stats:', err);
      });
  };

  useEffect(() => {
    fetchStats(); // Fetch stats when component mounts
  }, []);

  useEffect(() => {
    const ip = searchParams.get('ip');
    if (ip) {
      setFilters((prev) => ({ ...prev, ['organization_ip']: ip }));
      applyFilters();
    }
  }, [searchParams]);

  useEffect(() => {
    setAlerts([]); // Clear alerts when timeInterval changes
    setPage(0);
    setHasMore(true);
    fetchAlerts(0, true); // Fetch new alerts immediately when timeInterval changes
  }, [timeInterval]);

  useEffect(() => {
    if (page > 0) fetchAlerts(page); // Fetch additional pages when the page number changes
  }, [page]);

  useEffect(() => {
    applyFilters();
  }, [alerts, filters, stateFilter]);

  const fetchAlerts = (pageToFetch = page, reset = false) => {
    const token = localStorage.getItem('token');
    if (loading || (!hasMore && !reset)) return; // Prevent redundant fetches
    setLoading(true);
    const url = API_BASE_URL + `alerts?timeInterval=${timeInterval}&from=${pageToFetch * 20}&size=20`;
    fetch(url, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          console.error('Invalid data received:', data);
          return;
        }
        setAlerts((prevAlerts) =>
          reset ? data : [...prevAlerts, ...data]
        );
        setHasMore(data.length > 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching alerts:', err);
        setLoading(false);
      });
  };

  const applyFilters = () => {
    const filtered = alerts.filter((alert) => {
      const matchOrgHost =
        !filters.organization_hostname ||
        alert.organization_hostname.toLowerCase().includes(filters.organization_hostname.toLowerCase());
      const matchPeerHost =
        !filters.peer_hostname ||
        alert.peer_hostname.toLowerCase().includes(filters.peer_hostname.toLowerCase());
      const matchDescription =
        !filters.description ||
        alert.alert_description.toLowerCase().includes(filters.description.toLowerCase());
      const matchOrgIp =
        !filters.organization_ip ||
        alert.organization_ip === filters.organization_ip;

      var matchState = false;
      if (stateFilter === 'New') {
        matchState = alert.state == null;
      } else if (stateFilter === 'All') {
        matchState = true;
      } else if (stateFilter === 'Investigate' && alert.state === 'INV') {
        matchState = true;
      } else if (stateFilter === 'Closed' && alert.state === 'CLSD') {
        matchState = true;
      } else if (stateFilter === 'False Alarm' && alert.state === 'FA') {
        matchState = true;
      } else if (stateFilter === 'Not Closed' && (!alert.state || alert.state === 'INV')) {
        matchState = true;
      }

      return matchOrgHost && matchPeerHost && matchDescription && matchState && matchOrgIp;
    });
    setFilteredAlerts(filtered);
  };

  const clearFilters = () => {
    setFilters({
      organization_hostname: '',
      peer_hostname: '',
      description: '',
    });
  };

  const applyFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));

    // Apply filtering logic immediately
    const newFilteredAlerts = alerts.filter((alert) =>
      alert[field].toLowerCase().includes(value.toLowerCase())
    );
    setFilteredAlerts(newFilteredAlerts);
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    //  if (scrollTop + clientHeight >= scrollHeight - 50 && !loading && hasMore) {
    //    setPage((prevPage) => prevPage + 1);
    //  }
  };

  const handlePieChartClick = (event) => {
    if (!pieChartRef.current) return;
    const chart = pieChartRef.current;
    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);

    if (elements.length > 0) {
      const index = elements[0].index;
      const organizationHost = chart.data.labels[index];
      applyFilter('organization_hostname', organizationHost);
    }
  };

  const openAlertDetails = (alert) => {
    setSelectedAlert(alert);
  };

  const closeModal = () => {
    setSelectedAlert(null);
  };

  const openReasonModal = (alertId) => {
    setAlertToMarkFalse(alertId);
    setShowReasonModal(true);
  };

  const closeReasonModal = () => {
    setShowReasonModal(false);
    setReason('');
    setAlertToMarkFalse(null);
  };

  const handleChangeState = async (alertIds, action) => {
    const token = localStorage.getItem('token');
    const alertArray = Array.isArray(alertIds) ? alertIds : [alertIds];
    const alertIdsArray = alertArray.map((alert) => { return alert.id; });

    if (action === 'FA') {
      openReasonModal(alertIdsArray);
      return;
    }

    let url;
    const body = { alert_ids: alertIdsArray };
    switch (action) {
      case 'INV':
        url = `${API_BASE_URL}alerts/bulk/INV`;
        break;
      case 'CLSD':
        url = `${API_BASE_URL}alerts/bulk/CLSD`;
        break;
      case 'FA':
        url = `${API_BASE_URL}alerts/bulk/FA`;
        break;
      default:
        console.error('Invalid action:', action);
        return Promise.reject('Invalid action');
    }

    setBulkActionLoading(true); // Set loading state to true before the request

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        console.error(`Failed to update alerts: ${response.statusText}`);
        throw new Error(`Failed to update alerts`);
      }

      const data = await response.json();

      setAlerts((prevAlerts) =>
        prevAlerts.map((alert) =>
          alertIdsArray.includes(alert._id) ? { ...alert, state: action } : alert
        )
      );
      setSelectedAlerts([]);
    } catch (error) {
      console.error('Error updating alerts:', error);
    } finally {
      refreshNotifications(); // Refresh notifications after state change
      setBulkActionLoading(false); // Always reset loading state when done
    }
  };

  const handleBulkAction = async (action) => {
    handleChangeState(selectedAlerts, action);
  };

  const pieChartData = {
    labels: Object.keys(
      filteredAlerts.reduce((acc, alert) => {
        if (!acc[alert.organization_hostname]) acc[alert.organization_hostname] = 0;
        acc[alert.organization_hostname] += 1;
        return acc;
      }, {})
    ),
    datasets: [
      {
        data: Object.values(
          filteredAlerts.reduce((acc, alert) => {
            if (!acc[alert.organization_hostname]) acc[alert.organization_hostname] = 0;
            acc[alert.organization_hostname] += 1;
            return acc;
          }, {})
        ),
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
      },
    ],
  };

  const handleConfirmFalseAlarm = async () => {
    if (!alertToMarkFalse || reason.trim() === '') {
      alert('A justification is required.');
      return;
    }

    closeReasonModal();
    setBulkActionLoading(true); // Set loading state to true

    const token = localStorage.getItem('token');
    const url = `${API_BASE_URL}alerts/bulk/FA`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ alert_ids: alertToMarkFalse, reason: reason }),
      });

      if (!response.ok) {
        console.error(`Failed to update bulk alert ${alertToMarkFalse}: ${response.statusText}`);
        throw new Error(`Failed to update alert ${alertToMarkFalse}`);
      }

      const data = await response.json();

      setAlerts((prevAlerts) =>
        prevAlerts.map((alert) =>
          alertToMarkFalse.includes(alert._id) ? { ...alert, state: 'FA' } : alert
        )
      );
      setSelectedAlerts([]);
    } catch (error) {
      console.error('Error updating alerts:', error);
    } finally {
      refreshNotifications(); // Refresh notifications after state change
      setBulkActionLoading(false); // Reset loading state when done
    }
  };

  const refreshAlerts = () => {
    setPage(0);  // Reinicia a paginação
    setAlerts([]);  // Limpa os alertas antigos
    fetchAlerts(0, true);  // Faz o fetch novamente
    fetchStats(); // Also refresh stats
    refreshNotifications(); // Refresh notifications
  };

  return (
    <div>
      <h2>Alerts</h2>
      <TimeFilterButtons
        timeInterval={timeInterval}
        setTimeInterval={setTimeInterval}
      />

      {/* Dashboard Component */}
      <Dashboard
        stats={stats}
        filteredAlerts={alerts}
        timeInterval={timeInterval}
      />

      <div className="flex" style={{ justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button className="refresh-button" onClick={refreshAlerts}>
          Refresh Alerts
        </button>
      </div>

      <div className="d-flex" style={{ gap: '20px', alignItems: 'center' }}>
        <div style={{ flex: 1, maxWidth: '60%' }}>
          <Line
            data={{
              labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
              datasets: [
                {
                  label: 'Alerts per Hour',
                  data: filteredAlerts.reduce((acc, alert) => {
                    const hour = new Date(alert.timestamp).getHours();
                    acc[hour] = (acc[hour] || 0) + 1;
                    return acc;
                  }, Array(24).fill(0)),
                  borderColor: 'rgba(75,192,192,1)',
                  fill: false,
                },
              ],
            }}
          />
        </div>

        <div style={{ flex: 1, maxWidth: '40%' }}>
          <h5>Alerts by Host</h5>
          <div style={{ height: '300px', width: '300px' }}>
            <Pie
              ref={pieChartRef}
              data={pieChartData}
              options={{
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: (tooltipItem) => {
                        const count = tooltipItem.raw;
                        const percentage = ((count / filteredAlerts.length) * 100).toFixed(1);
                        return `${tooltipItem.label}: ${count} (${percentage}%)`;
                      },
                    },
                  },
                  legend: {
                    display: true,
                    position: 'bottom',
                  },
                },
              }}
              onClick={handlePieChartClick}
            />
          </div>
        </div>
      </div>

      <FiltersForm filters={filters} setFilters={setFilters} clearFilters={clearFilters} />

      <StateFilterButtons
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
        handleBulkAction={handleBulkAction}
        selectedAlerts={selectedAlerts}
        loading={bulkActionLoading} // Pass the bulk action loading state instead of general loading
      />

      {showReasonModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Justify False Alarm</h3>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter justification here..."
            />
            <button onClick={handleConfirmFalseAlarm} disabled={bulkActionLoading}>
              {bulkActionLoading ? 'Processing...' : 'Confirm'}
            </button>
            <button onClick={closeReasonModal} disabled={bulkActionLoading}>Cancel</button>
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #ddd',
        }}
      >
        <AlertsTable
          alerts={filteredAlerts}
          openAlertDetails={openAlertDetails}
          selectedAlerts={selectedAlerts}
          setSelectedAlerts={setSelectedAlerts}
        />
      </div>

      {selectedAlert && (
        <AlertDetailsModal
          alert={selectedAlert}
          onClose={closeModal}
          onApplyFilter={applyFilter}
          onChangeState={handleChangeState}
          loading={bulkActionLoading} // Pass the loading state to the modal as well
          allAlerts={alerts}
          refreshNotifications={refreshNotifications}
        />
      )}

      {/* Add a global loading indicator */}
      {bulkActionLoading && (
        <div className="loading-spinner-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default AlertsPage;
